import React, { useState } from 'react';
import { Campaign, campaigns } from '../data/demandData';
const statuses: (Campaign['status'] | 'All')[] = ['All', 'Live', 'Scheduled', 'Paused', 'Completed'];
const statusStyles: Record<Campaign['status'], string> = {
  Live: 'bg-forest/10 text-forest',
  Scheduled: 'bg-[#BB5E40]/12 text-[#94452E]',
  Paused: 'bg-ink/8 text-ink/60',
  Completed: 'bg-ink/8 text-ink/55'
};
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function DemandCampaigns({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [status, setStatus] = useState<Campaign['status'] | 'All'>('All');
  const rows = campaigns.filter(campaign => (!selectedOwner || campaign.brand === selectedOwner) && (status === 'All' || campaign.status === status));
  const spend = rows.reduce((sum, row) => sum + row.spend, 0);
  const revenue = rows.reduce((sum, row) => sum + row.revenue, 0);
  return <section aria-labelledby="campaigns-heading" data-id="element-540" data-mp="38oxi9qg98o44">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-541" data-mp="38oe8ix0rd616">
        <div data-id="element-542" data-mp="3dn8q5vmzoojp">
          <h2 id="campaigns-heading" className="font-display text-2xl font-medium tracking-tight" data-id="element-543" data-mp="3dmz0681y2he0">Campaigns</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-ink/60" data-id="element-544" data-mp="3if8wc5tnkdoq">Every paid and lifecycle push running against recovery inventory.</p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-545" data-mp="2pqbc3x5vs6tc">
          {statuses.map(option => <button key={option} type="button" onClick={() => setStatus(option)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${status === option ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 text-ink/60 hover:border-[#BB5E40]/40'}`} data-id="element-546" data-mp="2up66zxh4hltt">
              {option}
            </button>)}
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3" data-id="element-547" data-mp="jva4b2q87rid">
        <Tile label="Spend" value={currency(spend)} data-id="element-548" data-mp="2fzgel90lfyko" />
        <Tile label="Attributed revenue" value={currency(revenue)} data-id="element-549" data-mp="2g2cayswsmf70" />
        <Tile label="Blended ROAS" value={spend > 0 ? `${(revenue / spend).toFixed(1)}x` : '—'} data-id="element-550" data-mp="2b0dc9wc006ge" />
      </div>

      <div className="mt-5 overflow-hidden rounded-2xl border border-ink/10 bg-paper shadow-sm" data-id="element-551" data-mp="2p04uyadgmb0q">
        <div className="overflow-x-auto" data-id="element-552" data-mp="2kzuh6bjfegbb">
          <table className="w-full min-w-[820px] text-left text-sm" data-id="element-553" data-mp="1x9i8uzk9qhk7">
            <thead className="border-b border-ink/10 bg-cream/40 text-[11px] uppercase tracking-wider text-ink/50" data-id="element-554" data-mp="1x7nobmcwsrgn">
              <tr data-id="element-555" data-mp="2621yxbmhvdtf">
                <th scope="col" className="px-6 py-3 font-semibold" data-id="element-556" data-mp="262lj6yzmaeam">Campaign</th>
                <th scope="col" className="px-3 py-3 font-semibold" data-id="element-557" data-mp="21bday6a0wyz1">Brand</th>
                <th scope="col" className="px-3 py-3 font-semibold" data-id="element-558" data-mp="2198wiuei0hki">Channel</th>
                <th scope="col" className="px-3 py-3 font-semibold" data-id="element-559" data-mp="1dk7v3r4326oq">Window</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-560" data-mp="19j4gf7xrugp3">Spend</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-561" data-mp="zfz185ifxr86">Revenue</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-562" data-mp="147h6h5rhydyy">ROAS</th>
                <th scope="col" className="px-6 py-3 text-right font-semibold" data-id="element-563" data-mp="pib8fl4eatsn">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/8" data-id="element-564" data-mp="ui7avfxg2d63">
              {rows.length > 0 ? rows.map(row => <tr key={row.id} data-id="element-565" data-mp="1i81m5t247to8">
                  <th scope="row" className="px-6 py-4 text-left font-medium text-ink" data-id="element-566" data-mp="1i4vxug1b8vtk">{row.name}</th>
                  <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-567" data-mp="1n3gy2fgkbfi0">{row.brand}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-568" data-mp="18an6hvo0n8wz">{row.channel}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-569" data-mp="1d5sur14w4zo8">{row.window}</td>
                  <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-570" data-mp="3sxgn0j8ym40p">{row.spend > 0 ? currency(row.spend) : '—'}</td>
                  <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-571" data-mp="1q47s9rn00nf">{row.revenue > 0 ? currency(row.revenue) : '—'}</td>
                  <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-572" data-mp="d7vb8nu9h02v">{row.spend > 0 ? `${(row.revenue / row.spend).toFixed(1)}x` : '—'}</td>
                  <td className="px-6 py-4 text-right" data-id="element-573" data-mp="87h0gy19zc15">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusStyles[row.status]}`} data-id="element-574" data-mp="3c1oycqqps7w">{row.status}</span>
                  </td>
                </tr>) : <tr data-id="element-575" data-mp="vzxjpbwr1a21">
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-ink/55" data-id="element-576" data-mp="vxjqz9pjk9xn">No campaigns match this filter.</td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}
function Tile({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="rounded-2xl border border-ink/10 bg-paper p-4" data-id="element-577" data-mp="36rpap2ji912n">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-578" data-mp="36tjx6lafd18u">{label}</p>
      <p className="mt-1.5 font-display text-2xl font-semibold tracking-tight" data-id="element-579" data-mp="1mb804us6o5bl">{value}</p>
    </div>;
}